import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button, Input } from './ui/components';
import { Icon } from './ui/icons';
import { AuthShowcase, Eyebrow } from './pages/auth-redesign/showcase';
import { signInWithPasskey, isPasskeySupported } from './auth/passkey';

const IDLE_MINUTES = 30;
const API = process.env.REACT_APP_API_URL;

// Where a signed-in property account lands. Staff then unlock the shared terminal by PIN.
const POST_SIGNIN = '/lock';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  // Idle-logout / clock-out banners (carried over from the POS login flow)
  const [showIdleMessage, setShowIdleMessage] = useState(false);
  const [showClockOutSuccess, setShowClockOutSuccess] = useState(false);

  useEffect(() => {
    if (location.state?.idleLogout) {
      setShowIdleMessage(true);
      setShowClockOutSuccess(false);
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.clockOutSuccess) {
      setShowClockOutSuccess(true);
      setShowIdleMessage(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Passwordless, single screen (matches the admin site): email, then a passkey or an
  // emailed one-time link — both offered at once, no password anywhere.
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState<null | 'passkey' | 'link'>(null);
  const [linkSent, setLinkSent] = useState(false);
  const [showEmailMissingError, setShowEmailMissingError] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const passkeySupported = isPasskeySupported();

  const requireEmail = () => {
    if (email.trim() === '') {
      setShowEmailMissingError(true);
      return false;
    }
    setShowEmailMissingError(false);
    return true;
  };

  // Once a factor succeeds, the API has set the session cookie. If the account still
  // owes security setup, send them to the admin portal to finish it; otherwise into the app.
  const onSignedIn = (result: any) => {
    localStorage.setItem('propertyAccountEmail', email.trim().toLowerCase());
    login();
    if (result && result.setupRequired && result.setupRequired !== 'none') {
      navigate('/setup');
      return;
    }
    navigate(POST_SIGNIN);
  };

  const handlePasskey = async () => {
    if (!requireEmail()) return;
    setLoading('passkey');
    setShowError(false);
    setErrorMessage('');
    try {
      const result = await signInWithPasskey(email.trim());
      onSignedIn(result);
    } catch (err: any) {
      // A user cancelling the OS prompt is not an error worth shouting about.
      if (err && (err.name === 'NotAllowedError' || err.code === 'no_assertion')) {
        setErrorMessage('Passkey sign-in was cancelled.');
      } else {
        setErrorMessage(err?.message || 'Could not sign in with a passkey. Try the emailed link.');
      }
      setShowError(true);
    } finally {
      setLoading(null);
    }
  };

  const handleMagicLink = async () => {
    if (!requireEmail()) return;
    setLoading('link');
    setShowError(false);
    setErrorMessage('');
    try {
      const res = await fetch(`${API}/Authentication/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });
      // Always 200 with the same body — the server never reveals whether the email exists.
      if (res.ok) {
        setLinkSent(true);
      } else {
        setErrorMessage('Could not send the sign-in link. Please try again.');
        setShowError(true);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('An unexpected error occurred.');
      setShowError(true);
    } finally {
      setLoading(null);
    }
  };

  // Enter in the email field triggers the primary factor: a passkey where the device
  // has one, otherwise the emailed link.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passkeySupported) {
      handlePasskey();
    } else {
      handleMagicLink();
    }
  };

  return (
    <AuthShowcase>
      <form className="authx-form" onSubmit={handleSubmit}>
        <header className="authx-form__head">
          <Eyebrow>{linkSent ? 'Almost there' : 'Welcome back'}</Eyebrow>
          <h1 className="authx-form__title">{linkSent ? 'Check your email' : 'Sign in to TableView'}</h1>
          <p className="authx-form__sub">
            {linkSent
              ? 'A sign-in link is on its way if that address has an account. Open it on this device.'
              : 'Use a passkey (Face ID, fingerprint, or Windows Hello), or email yourself a one-time sign-in link. There is no password.'}
          </p>
        </header>

        {showIdleMessage && (
          <div className="authx-alert authx-alert--error" role="alert">
            <Icon.CircleAlert size={16} />
            <span>
              {`You have been automatically logged out due to inactivity of ${IDLE_MINUTES} minutes. Once you log in again, you should be able to resume the work where you left off.`}
            </span>
          </div>
        )}

        {showClockOutSuccess && (
          <div
            className="authx-alert"
            role="status"
            style={{ color: '#027A48', background: '#ECFDF3', borderColor: '#A6F4C5' }}
          >
            <Icon.CircleCheck size={16} />
            <span>You have been successfully clocked out. Have a great day!</span>
          </div>
        )}

        {showError && (
          <div className="authx-alert authx-alert--error" role="alert">
            <Icon.CircleAlert size={16} />
            <span>{errorMessage}</span>
          </div>
        )}

        {linkSent && (
          <div
            className="authx-alert"
            role="status"
            style={{ color: '#027A48', background: '#ECFDF3', borderColor: '#A6F4C5' }}
          >
            <Icon.CircleCheck size={16} />
            <span>
              If {email.trim()} has an account, we sent a one-time link. Open it on this device.
              It expires in 20 minutes — check junk if you don&apos;t see it.
            </span>
          </div>
        )}

        <div className="authx-form__fields">
          <Input
            label="Email"
            type="email"
            autoFocus
            value={email}
            placeholder="you@work.com"
            leadingIcon={<Icon.Mail size={16} />}
            error={showEmailMissingError ? 'Please enter your email address' : undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setEmail(e.target.value);
              if (showEmailMissingError) setShowEmailMissingError(false);
              if (linkSent) setLinkSent(false);
            }}
          />
        </div>

        <div className="authx-form__actions">
          {passkeySupported && (
            <Button
              type="button"
              variant="primary"
              fullWidth
              size="lg"
              loading={loading === 'passkey'}
              disabled={loading !== null}
              onClick={handlePasskey}
              leadingIcon={loading === 'passkey' ? undefined : <Icon.Lock size={16} />}
            >
              {loading === 'passkey' ? 'Waiting for passkey…' : 'Sign in with a passkey'}
            </Button>
          )}

          {passkeySupported && (
            <p className="authx-form__hint">
              Use Face ID, Touch ID, Windows Hello or your fingerprint. On a new device, your phone can approve a QR code.
            </p>
          )}

          {passkeySupported && (
            <div className="authx-divider"><span>or</span></div>
          )}

          <Button
            type={passkeySupported ? 'button' : 'submit'}
            variant={passkeySupported ? 'secondary' : 'primary'}
            fullWidth
            size="lg"
            loading={loading === 'link'}
            disabled={loading !== null}
            onClick={handleMagicLink}
            leadingIcon={loading === 'link' ? undefined : <Icon.Mail size={16} />}
          >
            {loading === 'link'
              ? 'Sending…'
              : linkSent
                ? 'Send another link'
                : 'Email me a sign-in link'}
          </Button>
        </div>
      </form>
    </AuthShowcase>
  );
};

export default Login;
